/**
 * Auto-layout that splits at the root condition’s true/false outputs, runs Dagre
 * inside each branch (and recursively for nested conditions), so unrelated lanes
 * don’t interleave like a single flat DAG.
 */
import dagre from 'dagre';
import type { Edge, Node } from '@xyflow/svelte';

/** Conservative node footprint for Dagre + bbox packing (real cards are often wider/taller than old defaults). */
const DAGRE_NODE_W = 340;
const DAGRE_NODE_H = 150;

/** Minimum horizontal gap between disconnected graph islands after layout. */
const ISLAND_GAP_X = 96;

/** Minimum Dagre separation between adjacent nodes (readability). */
const NODE_SEP_MIN = 48;

interface EdgeMaps {
	adjOut: Map<string, Edge[]>;
	adjIn: Map<string, Edge[]>;
}

function buildEdgeMaps(edges: Edge[]): EdgeMaps {
	const adjOut = new Map<string, Edge[]>();
	const adjIn = new Map<string, Edge[]>();
	for (const edge of edges) {
		if (!adjOut.has(edge.source)) adjOut.set(edge.source, []);
		adjOut.get(edge.source)!.push(edge);
		if (!adjIn.has(edge.target)) adjIn.set(edge.target, []);
		adjIn.get(edge.target)!.push(edge);
	}
	return { adjOut, adjIn };
}

const DECISION_TYPES = new Set([
	'trigger',
	'condition',
	'timeCondition',
	'manualCondition',
	'weatherCondition',
	'sunriseSunsetCondition',
	'energyCondition',
	'sceneCondition',
	'alarmCondition',
	'scheduleCondition',
	'variableCondition',
	'logicGate',
	'hysteresisCondition',
	'presenceCondition',
	'virtualComponentEvent',
	'virtualComponentCondition',
]);

function isDecisionNodeType(type?: string): boolean {
	return DECISION_TYPES.has(type || '');
}

function isBranchSourceHandle(handle: string | null | undefined): boolean {
	return (
		handle === 'true' ||
		handle === 'false' ||
		(typeof handle === 'string' && (handle.includes('true') || handle.includes('false')))
	);
}

function isGateOperandEdge(e: Edge): boolean {
	return typeof e.targetHandle === 'string' && e.targetHandle.startsWith('input-');
}

function isTrueHandle(handle: string | null | undefined): boolean {
	if (!handle) return false;
	if (handle === 'true') return true;
	if (handle === 'false') return false;
	return handle.includes('true') && !handle.includes('false');
}

function isFalseHandle(handle: string | null | undefined): boolean {
	if (!handle) return false;
	if (handle === 'false') return true;
	if (handle === 'true') return false;
	return handle.includes('false');
}

function findRootDecisionNode(nodes: Node[], em: EdgeMaps, nodeMap: Map<string, Node>): Node | null {
	const decisionNodes = nodes.filter((n) => isDecisionNodeType(n.type));

	const reachableViaDecisionBranch = new Set<string>();
	for (const node of decisionNodes) {
		for (const edge of em.adjOut.get(node.id) ?? []) {
			if (isBranchSourceHandle(edge.sourceHandle) && !isGateOperandEdge(edge)) {
				reachableViaDecisionBranch.add(edge.target);
			}
		}
	}

	const candidates = decisionNodes.filter((node) => {
		if (reachableViaDecisionBranch.has(node.id)) return false;
		const outgoing = em.adjOut.get(node.id) ?? [];
		const hasBranchOutputs = outgoing.some(
			(e) => isBranchSourceHandle(e.sourceHandle) && !isGateOperandEdge(e),
		);
		return hasBranchOutputs;
	});

	if (candidates.length > 0) {
		candidates.sort((a, b) => {
			const outA = (em.adjOut.get(a.id) ?? []).length;
			const outB = (em.adjOut.get(b.id) ?? []).length;
			if (outB !== outA) return outB - outA;
			const inA = (em.adjIn.get(a.id) ?? []).filter(
				(e) => nodeMap.get(e.source)?.type === 'trigger',
			).length;
			const inB = (em.adjIn.get(b.id) ?? []).filter(
				(e) => nodeMap.get(e.source)?.type === 'trigger',
			).length;
			if (inB !== inA) return inB - inA;
			return (a.position?.x ?? 0) - (b.position?.x ?? 0);
		});
		return candidates[0];
	}

	return decisionNodes[0] || null;
}

function branchTargets(rootId: string, which: 'true' | 'false', em: EdgeMaps): string[] {
	const targets: string[] = [];
	for (const e of em.adjOut.get(rootId) ?? []) {
		if (isGateOperandEdge(e)) continue;
		if (which === 'true' && isTrueHandle(e.sourceHandle)) targets.push(e.target);
		else if (which === 'false' && isFalseHandle(e.sourceHandle)) targets.push(e.target);
	}
	return targets;
}

function bfsDownstream(seeds: string[], em: EdgeMaps, allowed: Set<string>): Set<string> {
	const vis = new Set<string>();
	const q = seeds.filter((id) => allowed.has(id));
	for (const id of q) vis.add(id);

	while (q.length) {
		const id = q.shift()!;
		for (const e of em.adjOut.get(id) ?? []) {
			if (!allowed.has(e.target) || vis.has(e.target)) continue;
			vis.add(e.target);
			q.push(e.target);
		}
	}
	return vis;
}

function setDiff(a: Set<string>, b: Set<string>): Set<string> {
	const o = new Set<string>();
	for (const x of a) if (!b.has(x)) o.add(x);
	return o;
}

function setIntersect(a: Set<string>, b: Set<string>): Set<string> {
	const o = new Set<string>();
	for (const x of a) if (b.has(x)) o.add(x);
	return o;
}

function subgraphEdges(edgeList: Edge[], idSet: Set<string>): Edge[] {
	return edgeList.filter((e) => idSet.has(e.source) && idSet.has(e.target));
}

/** Dagre LR layout; xyflow expects top-left of node at center offset used in FlowCanvas. */
function runDagreLayout(nodeList: Node[], edgeList: Edge[]): Node[] {
	if (nodeList.length === 0) return [];

	const g = new dagre.graphlib.Graph();
	g.setDefaultEdgeLabel(() => ({}));
	g.setGraph({
		rankdir: 'LR',
		nodesep: Math.max(88, NODE_SEP_MIN),
		ranksep: Math.max(150, NODE_SEP_MIN),
	});

	for (const node of nodeList) {
		g.setNode(node.id, { width: DAGRE_NODE_W, height: DAGRE_NODE_H });
	}
	for (const edge of edgeList) {
		g.setEdge(edge.source, edge.target);
	}

	dagre.layout(g);

	return nodeList.map((node) => {
		const pos = g.node(node.id);
		return {
			...node,
			position: {
				x: pos.x - DAGRE_NODE_W / 2,
				y: pos.y - DAGRE_NODE_H / 2,
			},
		};
	});
}

function bboxOfPositions(pos: Map<string, { x: number; y: number }>) {
	let minX = Infinity,
		maxX = -Infinity,
		minY = Infinity,
		maxY = -Infinity;
	for (const p of pos.values()) {
		minX = Math.min(minX, p.x);
		maxX = Math.max(maxX, p.x + DAGRE_NODE_W);
		minY = Math.min(minY, p.y);
		maxY = Math.max(maxY, p.y + DAGRE_NODE_H);
	}
	if (!Number.isFinite(minX)) return { minX: 0, maxX: 0, minY: 0, maxY: 0, cx: 0, cy: 0 };
	return {
		minX,
		maxX,
		minY,
		maxY,
		cx: (minX + maxX) / 2,
		cy: (minY + maxY) / 2,
	};
}

/** Dagre sub-layout aligned: left edge at anchor.x, vertical center at anchor.y. */
function runDagreAnchored(subNodes: Node[], subEdges: Edge[], anchor: { x: number; y: number }) {
	if (subNodes.length === 0) return new Map<string, { x: number; y: number }>();
	const laid = runDagreLayout(subNodes, subEdges);
	const raw = new Map<string, { x: number; y: number }>();
	for (const n of laid) raw.set(n.id, { ...n.position });

	const box = bboxOfPositions(raw);
	const dx = anchor.x - box.minX;
	const dy = anchor.y - box.cy;

	const out = new Map<string, { x: number; y: number }>();
	raw.forEach((p, id) => out.set(id, { x: p.x + dx, y: p.y + dy }));
	return out;
}

const STEP_X = 360;
const LANE_Y = 180;
const MAX_RECURSION = 24;

function layoutChunk(
	subNodes: Node[],
	subEdges: Edge[],
	anchor: { x: number; y: number },
	depth: number,
): Map<string, { x: number; y: number }> {
	const out = new Map<string, { x: number; y: number }>();
	if (subNodes.length === 0) return out;

	const idSet = new Set(subNodes.map((n) => n.id));
	const em = buildEdgeMaps(subEdges);
	const nodeMap = new Map(subNodes.map((n) => [n.id, n]));
	const root = findRootDecisionNode(subNodes, em, nodeMap);

	const seedsT = root ? branchTargets(root.id, 'true', em).filter((id) => idSet.has(id)) : [];
	const seedsF = root ? branchTargets(root.id, 'false', em).filter((id) => idSet.has(id)) : [];
	const shouldSplit =
		depth < MAX_RECURSION &&
		root &&
		subNodes.length > 1 &&
		(seedsT.length > 0 || seedsF.length > 0) &&
		(seedsT.length === 0 || seedsF.length === 0 ? subNodes.length > 2 : true);

	if (!shouldSplit) {
		return runDagreAnchored(subNodes, subEdges, anchor);
	}

	const rootId = root!.id;
	const T = bfsDownstream(seedsT, em, idSet);
	const F = bfsDownstream(seedsF, em, idSet);
	const onlyT = setDiff(T, F);
	const onlyF = setDiff(F, T);
	const merge = setIntersect(T, F);
	onlyT.delete(rootId);
	onlyF.delete(rootId);
	merge.delete(rootId);

	out.set(rootId, { ...anchor });

	const placeSub = (
		ids: Set<string>,
		subAnchor: { x: number; y: number },
	): Map<string, { x: number; y: number }> => {
		if (ids.size === 0) return new Map();
		const nodesSub = subNodes.filter((n) => ids.has(n.id));
		const edgesSub = subgraphEdges(subEdges, new Set(nodesSub.map((n) => n.id)));
		return layoutChunk(nodesSub, edgesSub, subAnchor, depth + 1);
	};

	const rightX = anchor.x + STEP_X;
	const mT = placeSub(onlyT, { x: rightX, y: anchor.y - LANE_Y });
	const mF = placeSub(onlyF, { x: rightX, y: anchor.y + LANE_Y });
	const mM = placeSub(merge, { x: rightX + STEP_X * 0.35, y: anchor.y });

	for (const m of [mT, mF, mM]) {
		m.forEach((pos, id) => out.set(id, pos));
	}

	for (const n of subNodes) {
		if (!out.has(n.id)) {
			out.set(n.id, { ...(n.position ?? { x: anchor.x + STEP_X * 2, y: anchor.y }) });
		}
	}

	return out;
}

function hasSwitchNode(nodeList: Node[]): boolean {
	return nodeList.some((n) => n.type === 'switchNode');
}

/** Weakly connected components (undirected) — each island laid out independently for spacing. */
function weaklyConnectedComponents(nodes: Node[], edges: Edge[]): Set<string>[] {
	const adj = new Map<string, Set<string>>();
	for (const n of nodes) adj.set(n.id, new Set());
	for (const e of edges) {
		adj.get(e.source)?.add(e.target);
		adj.get(e.target)?.add(e.source);
	}
	const visited = new Set<string>();
	const comps: Set<string>[] = [];

	for (const n of nodes) {
		if (visited.has(n.id)) continue;
		const stack = [n.id];
		visited.add(n.id);
		const comp = new Set<string>([n.id]);
		while (stack.length) {
			const u = stack.pop()!;
			for (const v of adj.get(u) ?? []) {
				if (!visited.has(v)) {
					visited.add(v);
					stack.push(v);
					comp.add(v);
				}
			}
		}
		comps.push(comp);
	}
	return comps;
}

function bboxOfNodeSubset(nodes: Node[], idSet: Set<string>) {
	let minX = Infinity,
		maxX = -Infinity,
		minY = Infinity,
		maxY = -Infinity;
	for (const n of nodes) {
		if (!idSet.has(n.id)) continue;
		const x = n.position?.x ?? 0;
		const y = n.position?.y ?? 0;
		minX = Math.min(minX, x);
		maxX = Math.max(maxX, x + DAGRE_NODE_W);
		minY = Math.min(minY, y);
		maxY = Math.max(maxY, y + DAGRE_NODE_H);
	}
	if (!Number.isFinite(minX)) return { minX: 0, maxX: 0, minY: 0, maxY: 0 };
	return { minX, maxX, minY, maxY };
}

/**
 * After Dagre / branch layout, separate disconnected islands horizontally with {@link ISLAND_GAP_X}.
 */
export function packDisconnectedIslands(nodes: Node[], edges: Edge[]): Node[] {
	const comps = weaklyConnectedComponents(nodes, edges);
	if (comps.length <= 1) return nodes;

	comps.sort((a, b) => bboxOfNodeSubset(nodes, a).minX - bboxOfNodeSubset(nodes, b).minX);

	let cursorRight = Number.NEGATIVE_INFINITY;
	let result = [...nodes];
	let first = true;

	for (const comp of comps) {
		const bbox = bboxOfNodeSubset(result, comp);
		const gap = first ? 0 : ISLAND_GAP_X;
		first = false;
		const targetLeft = cursorRight === Number.NEGATIVE_INFINITY ? bbox.minX : cursorRight + gap;
		const dx = targetLeft - bbox.minX;

		result = result.map((n) =>
			comp.has(n.id)
				? { ...n, position: { x: (n.position?.x ?? 0) + dx, y: n.position?.y ?? 0 } }
				: n,
		);

		const nb = bboxOfNodeSubset(result, comp);
		cursorRight = nb.maxX;
	}

	return result;
}

/**
 * Triggers are laid out in a left column; the rest of the graph uses branch-aware layout
 * starting at (320, 0). Switch-heavy graphs fall back to flat Dagre (case-chain layout).
 */
export function applyBranchAwareLayout(
	nodeList: Node[],
	edgeList: Edge[],
): { nodes: Node[]; edges: Edge[] } {
	if (nodeList.length === 0) return { nodes: [], edges: edgeList };

	if (hasSwitchNode(nodeList)) {
		const nodes = packDisconnectedIslands(runDagreLayout(nodeList, edgeList), edgeList);
		return { nodes, edges: edgeList };
	}

	const triggers = nodeList.filter((n) => n.type === 'trigger');
	const nonTriggerIds = new Set(nodeList.filter((n) => n.type !== 'trigger').map((n) => n.id));
	const mainEdges = subgraphEdges(edgeList, nonTriggerIds);

	const positions = new Map<string, { x: number; y: number }>();

	const mainNodes = nodeList.filter((n) => n.type !== 'trigger');
	if (mainNodes.length > 0) {
		const chunkPos = layoutChunk(mainNodes, mainEdges, { x: 320, y: 0 }, 0);
		chunkPos.forEach((pos, id) => positions.set(id, pos));
	}

	const tLen = triggers.length;
	triggers.forEach((t, i) => {
		positions.set(t.id, { x: 0, y: i * 90 - ((tLen - 1) * 90) / 2 });
	});

	for (const n of nodeList) {
		if (!positions.has(n.id)) {
			positions.set(n.id, n.position ?? { x: 400, y: 0 });
		}
	}

	const layoutedNodes = nodeList.map((n) => ({
		...n,
		position: positions.get(n.id)!,
	}));

	const packedNodes = packDisconnectedIslands(layoutedNodes, edgeList);

	return { nodes: packedNodes, edges: edgeList };
}
